/**
 * Люди, техника и материалы в строке отчёта смены (ПР-02, ПР-06 → склад).
 * Телематика за день — подстановка маш.-ч (ПР-02 ← РС-16).
 */
import type { SiteReportDraft, SiteTelemetryReading } from '@epc/contracts';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Field, Input, Select } from '../../ui/form';

export type SiteMachineOption = { machineId: string; name: string; regNumber: string | null };
export type SiteWorkerOption = { workerId: string; fullName: string; personnelNo: string | null };
export type SiteMaterialOption = {
  itemId: string;
  name: string;
  unit: string;
  warehouseId: string;
  available: number;
};
export type SiteWarehouseOption = { warehouseId: string; name: string };
type Line = SiteReportDraft['lines'][number];

function patchLine(draft: SiteReportDraft, lineId: string, next: Line): SiteReportDraft {
  return { ...draft, lines: draft.lines.map((x) => (x.id === lineId ? next : x)) };
}

/** Количество из показания телематики в строку отчёта (не больше 24 ч). */
export function qtyFromTelemetry(n: number | null | undefined): string | null {
  if (n == null || !Number.isFinite(n) || n < 0) return null;
  const v = Math.min(24, Math.round(n * 10) / 10);
  return Number.isInteger(v) ? String(v) : String(v);
}

/** Расхождение с телематикой: больше получаса и 10 % (как mismatchOf на сервере). */
export function telemetryMismatch(entered: string, reading: number | null | undefined): boolean {
  if (reading == null || !Number.isFinite(reading)) return false;
  const a = Number(entered.replace(',', '.'));
  if (!Number.isFinite(a)) return false;
  return Math.abs(a - reading) > Math.max(0.5, Math.abs(reading) * 0.1);
}

export function LineResources({
  draft,
  line,
  editable,
  machines,
  workers,
  warehouses,
  materialItems,
  telemetry,
  onChange,
}: {
  draft: SiteReportDraft;
  line: Line;
  editable: boolean;
  machines: SiteMachineOption[];
  workers: SiteWorkerOption[];
  warehouses: SiteWarehouseOption[];
  materialItems: SiteMaterialOption[];
  telemetry: SiteTelemetryReading[];
  onChange: (next: SiteReportDraft) => void;
}) {
  const set = (next: Line) => onChange(patchLine(draft, line.id, next));
  const activeMachines = machines;
  const telByMachine = new Map(telemetry.map((t) => [t.machineId, t]));
  const applyTelemetry = (machineId: string) => {
    const t = telByMachine.get(machineId);
    if (!t) return;
    const hours = qtyFromTelemetry(t.hours) ?? '0';
    const idleHours = qtyFromTelemetry(t.idleHours) ?? '0';
    const ref = { type: 'machine_telemetry', id: t.id, revision: 0 };
    const existing = line.machines.findIndex((x) => x.machineId === machineId);
    if (existing >= 0) {
      set({
        ...line,
        machines: line.machines.map((x, j) =>
          j === existing
            ? {
                ...x,
                hours,
                idleHours,
                idleReason: idleHours !== '0' ? (x.idleReason ?? 'По телематике') : null,
                telemetryRef: ref,
              }
            : x,
        ),
      });
      return;
    }
    set({
      ...line,
      machines: [
        ...line.machines,
        {
          id: crypto.randomUUID(),
          machineId,
          hours,
          idleHours,
          idleReason: idleHours !== '0' ? 'По телематике' : null,
          telemetryRef: ref,
        },
      ],
    });
  };

  return (
    <div className="mt-4 grid gap-4">
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-[13px] font-semibold text-ink">Люди</h4>
          {editable ? (
            <Button
              size="sm"
              icon={<Plus className="size-4" />}
              onClick={() =>
                set({
                  ...line,
                  labor: [
                    ...line.labor,
                    {
                      id: crypto.randomUUID(),
                      workerId: null,
                      profession: '',
                      headcount: 1,
                      hours: '8',
                      idleHours: '0',
                      idleReason: null,
                    },
                  ],
                })
              }
            >
              Добавить
            </Button>
          ) : null}
        </div>
        {!line.labor.length ? (
          <p className="text-[13px] text-ink-2">Профессии и часы за смену пока не указаны.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {line.labor.map((lab, i) => (
              <li key={lab.id} className="rounded-md border border-line bg-surface-2 p-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Работник" htmlFor={`lab-w-${lab.id}`} className="sm:col-span-2">
                    <Select
                      id={`lab-w-${lab.id}`}
                      className="!h-11"
                      value={lab.workerId ?? ''}
                      disabled={!editable}
                      onChange={(e) => {
                        const workerId = e.target.value || null;
                        set({
                          ...line,
                          labor: line.labor.map((x, j) =>
                            j === i
                              ? {
                                  ...x,
                                  workerId,
                                  headcount: workerId ? 1 : x.headcount,
                                }
                              : x,
                          ),
                        });
                      }}
                    >
                      <option value="">Без привязки к табелю (только профессия)</option>
                      {workers.map((w) => (
                        <option key={w.workerId} value={w.workerId}>
                          {w.fullName}
                          {w.personnelNo ? ` · ${w.personnelNo}` : ''}
                        </option>
                      ))}
                      {lab.workerId && !workers.some((w) => w.workerId === lab.workerId) ? (
                        <option value={lab.workerId}>Работник (нет в списке проекта)</option>
                      ) : null}
                    </Select>
                  </Field>
                  <Field label="Профессия" htmlFor={`lab-prof-${lab.id}`}>
                    <Input
                      id={`lab-prof-${lab.id}`}
                      className="!h-11"
                      value={lab.profession}
                      disabled={!editable}
                      placeholder="Например, бетонщик"
                      onChange={(e) =>
                        set({
                          ...line,
                          labor: line.labor.map((x, j) =>
                            j === i ? { ...x, profession: e.target.value } : x,
                          ),
                        })
                      }
                    />
                  </Field>
                  <Field label="Численность" htmlFor={`lab-n-${lab.id}`}>
                    <Input
                      id={`lab-n-${lab.id}`}
                      className="!h-11"
                      inputMode="numeric"
                      value={String(lab.headcount)}
                      disabled={!editable || Boolean(lab.workerId)}
                      onChange={(e) => {
                        const n = Number(e.target.value.replace(/\D/g, '')) || 1;
                        set({
                          ...line,
                          labor: line.labor.map((x, j) =>
                            j === i ? { ...x, headcount: Math.min(10_000, Math.max(1, n)) } : x,
                          ),
                        });
                      }}
                    />
                  </Field>
                  <Field label="Всего чел.-ч" htmlFor={`lab-h-${lab.id}`}>
                    <Input
                      id={`lab-h-${lab.id}`}
                      className="!h-11"
                      inputMode="decimal"
                      value={lab.hours}
                      disabled={!editable}
                      onChange={(e) =>
                        set({
                          ...line,
                          labor: line.labor.map((x, j) =>
                            j === i ? { ...x, hours: e.target.value.replace(',', '.') } : x,
                          ),
                        })
                      }
                    />
                  </Field>
                  <Field label="Из них простой" htmlFor={`lab-idle-${lab.id}`}>
                    <Input
                      id={`lab-idle-${lab.id}`}
                      className="!h-11"
                      inputMode="decimal"
                      value={lab.idleHours}
                      disabled={!editable}
                      onChange={(e) =>
                        set({
                          ...line,
                          labor: line.labor.map((x, j) =>
                            j === i ? { ...x, idleHours: e.target.value.replace(',', '.') } : x,
                          ),
                        })
                      }
                    />
                  </Field>
                  {lab.idleHours !== '0' && lab.idleHours !== '' ? (
                    <Field label="Причина простоя" htmlFor={`lab-reason-${lab.id}`} className="sm:col-span-2">
                      <Input
                        id={`lab-reason-${lab.id}`}
                        className="!h-11"
                        value={lab.idleReason ?? ''}
                        disabled={!editable}
                        onChange={(e) =>
                          set({
                            ...line,
                            labor: line.labor.map((x, j) =>
                              j === i
                                ? { ...x, idleReason: e.target.value.trim() ? e.target.value : null }
                                : x,
                            ),
                          })
                        }
                      />
                    </Field>
                  ) : null}
                </div>
                {editable ? (
                  <Button
                    className="mt-3"
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 className="size-4" />}
                    onClick={() => set({ ...line, labor: line.labor.filter((_, j) => j !== i) })}
                  >
                    Убрать
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-[13px] font-semibold text-ink">Техника</h4>
          {editable ? (
            <Button
              size="sm"
              icon={<Plus className="size-4" />}
              disabled={!activeMachines.length}
              onClick={() => {
                const free = activeMachines.find(
                  (m) => !line.machines.some((x) => x.machineId === m.machineId),
                );
                const pick = free ?? activeMachines[0];
                if (!pick) return;
                set({
                  ...line,
                  machines: [
                    ...line.machines,
                    {
                      id: crypto.randomUUID(),
                      machineId: pick.machineId,
                      hours: '8',
                      idleHours: '0',
                      idleReason: null,
                      telemetryRef: null,
                    },
                  ],
                });
              }}
            >
              Добавить
            </Button>
          ) : null}
        </div>
        {!activeMachines.length && editable ? (
          <p className="text-[13px] text-ink-2">
            На проекте нет активной техники в парке — сначала заведите единицу в разделе «Техника».
          </p>
        ) : null}
        {telemetry.length && editable ? (
          <div className="mb-3 rounded-md border border-line bg-surface p-3">
            <p className="text-[13px] text-ink-2">
              За день есть показания телематики — можно подставить моточасы и простой в строку.
            </p>
            <ul className="mt-2 flex flex-col gap-2">
              {telemetry.map((t) => {
                const parts: string[] = [];
                if (t.hours != null) parts.push(`работа ${qtyFromTelemetry(t.hours)} ч`);
                if (t.idleHours != null) parts.push(`простой ${qtyFromTelemetry(t.idleHours)} ч`);
                if (t.fuelLitres != null) parts.push(`топливо ${t.fuelLitres} л`);
                if (t.distanceKm != null) parts.push(`пробег ${t.distanceKm} км`);
                return (
                  <li
                    key={t.id}
                    className="flex flex-wrap items-center justify-between gap-2 text-[13px] text-ink"
                  >
                    <span className="min-w-0 break-words">
                      {t.machineName}
                      {t.regNumber ? ` · ${t.regNumber}` : ''}
                      {parts.length ? ` — ${parts.join(', ')}` : ' — без часов за день'}
                    </span>
                    <Button
                      size="sm"
                      className="min-h-11 shrink-0"
                      disabled={t.hours == null && t.idleHours == null}
                      onClick={() => applyTelemetry(t.machineId)}
                    >
                      Подставить
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        {!line.machines.length ? (
          <p className="text-[13px] text-ink-2">Машино-часы за смену пока не указаны.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {line.machines.map((m, i) => {
              const tel = telByMachine.get(m.machineId);
              const hoursOff = tel ? telemetryMismatch(m.hours, tel.hours) : false;
              const idleOff = tel ? telemetryMismatch(m.idleHours, tel.idleHours) : false;
              return (
                <li key={m.id} className="rounded-md border border-line bg-surface-2 p-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Машина" htmlFor={`mch-${m.id}`} className="sm:col-span-2">
                      <Select
                        id={`mch-${m.id}`}
                        className="!h-11"
                        value={m.machineId}
                        disabled={!editable}
                        onChange={(e) =>
                          set({
                            ...line,
                            machines: line.machines.map((x, j) =>
                              j === i ? { ...x, machineId: e.target.value, telemetryRef: null } : x,
                            ),
                          })
                        }
                      >
                        {activeMachines.map((u) => (
                          <option key={u.machineId} value={u.machineId}>
                            {u.name}
                            {u.regNumber ? ` · ${u.regNumber}` : ''}
                          </option>
                        ))}
                        {!activeMachines.some((u) => u.machineId === m.machineId) ? (
                          <option value={m.machineId}>Техника (нет в списке)</option>
                        ) : null}
                      </Select>
                    </Field>
                    <Field label="Маш.-ч работы" htmlFor={`mch-h-${m.id}`}>
                      <Input
                        id={`mch-h-${m.id}`}
                        className="!h-11"
                        inputMode="decimal"
                        value={m.hours}
                        disabled={!editable}
                        onChange={(e) =>
                          set({
                            ...line,
                            machines: line.machines.map((x, j) =>
                              j === i
                                ? {
                                    ...x,
                                    hours: e.target.value.replace(',', '.'),
                                    telemetryRef: null,
                                  }
                                : x,
                            ),
                          })
                        }
                      />
                    </Field>
                    <Field label="Простой" htmlFor={`mch-idle-${m.id}`}>
                      <Input
                        id={`mch-idle-${m.id}`}
                        className="!h-11"
                        inputMode="decimal"
                        value={m.idleHours}
                        disabled={!editable}
                        onChange={(e) =>
                          set({
                            ...line,
                            machines: line.machines.map((x, j) =>
                              j === i
                                ? {
                                    ...x,
                                    idleHours: e.target.value.replace(',', '.'),
                                    telemetryRef: null,
                                  }
                                : x,
                            ),
                          })
                        }
                      />
                    </Field>
                    {m.idleHours !== '0' && m.idleHours !== '' ? (
                      <Field label="Причина простоя" htmlFor={`mch-reason-${m.id}`} className="sm:col-span-2">
                        <Input
                          id={`mch-reason-${m.id}`}
                          className="!h-11"
                          value={m.idleReason ?? ''}
                          disabled={!editable}
                          onChange={(e) =>
                            set({
                              ...line,
                              machines: line.machines.map((x, j) =>
                                j === i
                                  ? {
                                      ...x,
                                      idleReason: e.target.value.trim() ? e.target.value : null,
                                    }
                                  : x,
                              ),
                            })
                          }
                        />
                      </Field>
                    ) : null}
                  </div>
                  {hoursOff || idleOff ? (
                    <p className="mt-2 text-[13px] text-warning">
                      Расходится с телематикой
                      {tel?.hours != null ? ` (работа ${qtyFromTelemetry(tel.hours)} ч` : ''}
                      {tel?.idleHours != null
                        ? `${tel.hours != null ? ', ' : ' ('}простой ${qtyFromTelemetry(tel.idleHours)} ч`
                        : ''}
                      {tel?.hours != null || tel?.idleHours != null ? ')' : ''}.
                    </p>
                  ) : null}
                  {editable ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {tel && (tel.hours != null || tel.idleHours != null) ? (
                        <Button size="sm" onClick={() => applyTelemetry(m.machineId)}>
                          Из телематики
                        </Button>
                      ) : null}
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Trash2 className="size-4" />}
                        onClick={() => set({ ...line, machines: line.machines.filter((_, j) => j !== i) })}
                      >
                        Убрать
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-[13px] font-semibold text-ink">Материалы</h4>
          {editable ? (
            <Button
              size="sm"
              icon={<Plus className="size-4" />}
              disabled={!materialItems.length}
              onClick={() => {
                const pick = materialItems[0];
                if (!pick) return;
                set({
                  ...line,
                  materials: [
                    ...line.materials,
                    {
                      id: crypto.randomUUID(),
                      warehouseId: pick.warehouseId,
                      itemId: pick.itemId,
                      quantity: '1',
                      unit: pick.unit,
                      normRef: null,
                      issueAllocations: [],
                    },
                  ],
                });
              }}
            >
              Добавить
            </Button>
          ) : null}
        </div>
        {!warehouses.length && editable ? (
          <p className="text-[13px] text-ink-2">
            Нет склада проекта или общекомпанийского — сначала заведите склад в разделе «Склад».
          </p>
        ) : null}
        {warehouses.length && !materialItems.length && editable ? (
          <p className="text-[13px] text-ink-2">
            На складах проекта нет доступного остатка — списать пока нечего.
          </p>
        ) : null}
        {!line.materials.length ? (
          <p className="text-[13px] text-ink-2">Расход материалов за смену пока не указан.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {line.materials.map((m, i) => {
              const options = materialItems.filter((x) => x.warehouseId === m.warehouseId);
              const selected = materialItems.find(
                (x) => x.warehouseId === m.warehouseId && x.itemId === m.itemId,
              );
              return (
                <li key={m.id} className="rounded-md border border-line bg-surface-2 p-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="Склад" htmlFor={`mat-wh-${m.id}`} className="sm:col-span-2">
                      <Select
                        id={`mat-wh-${m.id}`}
                        className="!h-11"
                        value={m.warehouseId}
                        disabled={!editable}
                        onChange={(e) => {
                          const warehouseId = e.target.value;
                          const first = materialItems.find((x) => x.warehouseId === warehouseId);
                          set({
                            ...line,
                            materials: line.materials.map((x, j) =>
                              j === i
                                ? {
                                    ...x,
                                    warehouseId,
                                    itemId: first?.itemId ?? x.itemId,
                                    unit: first?.unit ?? x.unit,
                                  }
                                : x,
                            ),
                          });
                        }}
                      >
                        {warehouses.map((w) => (
                          <option key={w.warehouseId} value={w.warehouseId}>
                            {w.name}
                          </option>
                        ))}
                        {!warehouses.some((w) => w.warehouseId === m.warehouseId) ? (
                          <option value={m.warehouseId}>Склад (нет в списке)</option>
                        ) : null}
                      </Select>
                    </Field>
                    <Field label="Позиция" htmlFor={`mat-it-${m.id}`} className="sm:col-span-2">
                      <Select
                        id={`mat-it-${m.id}`}
                        className="!h-11"
                        value={m.itemId}
                        disabled={!editable}
                        onChange={(e) => {
                          const itemId = e.target.value;
                          const pick = options.find((x) => x.itemId === itemId);
                          set({
                            ...line,
                            materials: line.materials.map((x, j) =>
                              j === i ? { ...x, itemId, unit: pick?.unit ?? x.unit } : x,
                            ),
                          });
                        }}
                      >
                        {options.map((it) => (
                          <option key={it.itemId} value={it.itemId}>
                            {it.name} · доступно {it.available} {it.unit}
                          </option>
                        ))}
                        {!options.some((it) => it.itemId === m.itemId) ? (
                          <option value={m.itemId}>{selected?.name ?? 'Позиция (нет в остатке)'}</option>
                        ) : null}
                      </Select>
                    </Field>
                    <Field label={`Количество, ${m.unit}`} htmlFor={`mat-q-${m.id}`}>
                      <Input
                        id={`mat-q-${m.id}`}
                        className="!h-11"
                        inputMode="decimal"
                        value={m.quantity}
                        disabled={!editable}
                        onChange={(e) =>
                          set({
                            ...line,
                            materials: line.materials.map((x, j) =>
                              j === i ? { ...x, quantity: e.target.value.replace(',', '.') } : x,
                            ),
                          })
                        }
                      />
                    </Field>
                  </div>
                  {editable ? (
                    <Button
                      className="mt-3"
                      size="sm"
                      variant="ghost"
                      icon={<Trash2 className="size-4" />}
                      onClick={() => set({ ...line, materials: line.materials.filter((_, j) => j !== i) })}
                    >
                      Убрать
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
